import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Lock,
  Payment,
  AccessTime,
  Business,
  Star,
  Security
} from '@mui/icons-material';
import { useAuth } from '../services/auth';

const UserAuthorization = ({ user, onAuthorized, onUnauthorized }) => {
  const [loading, setLoading] = useState(true);
  const [authorizationStatus, setAuthorizationStatus] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const { user: authUser } = useAuth();

  useEffect(() => {
    checkAuthorization();
  }, [user]);

  const checkAuthorization = async () => {
    setLoading(true);
    
    try {
      // Check if user has a valid subscription
      const userProfile = user?.user_metadata || {};
      const subscriptionTier = userProfile.subscription_tier || 'free';
      const subscriptionStatus = userProfile.subscription_status || 'inactive';
      
      // Check if user has completed payment verification
      const isPaymentVerified = userProfile.payment_verified || false;
      const trialEndsAt = userProfile.trial_ends_at;
      const isTrialActive = trialEndsAt ? new Date(trialEndsAt) > new Date() : false;
      
      const status = {
        isAuthorized: false,
        subscriptionTier,
        subscriptionStatus,
        isPaymentVerified,
        isTrialActive,
        trialEndsAt,
        message: '',
        requiresAction: false
      };

      // Determine authorization status
      if (subscriptionTier === 'premium' && isPaymentVerified) {
        status.isAuthorized = true;
        status.message = 'Premium account active';
      } else if (subscriptionTier === 'professional' && isPaymentVerified) {
        status.isAuthorized = true;
        status.message = 'Professional account active';
      } else if (isTrialActive) {
        status.isAuthorized = true;
        status.message = 'Trial account active';
      } else if (subscriptionTier === 'free') {
        status.isAuthorized = true;
        status.message = 'Free account active (limited features)';
      } else {
        status.isAuthorized = false;
        status.requiresAction = true;
        status.message = 'Payment verification required';
      }

      setAuthorizationStatus(status);
      
      if (status.isAuthorized) {
        onAuthorized(user, status);
      } else {
        onUnauthorized(user, status);
      }

    } catch (error) {
      console.error('Authorization check failed:', error);
      setAuthorizationStatus({
        isAuthorized: false,
        message: 'Authorization check failed',
        requiresAction: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    // Redirect to payment/upgrade page
    window.open('/upgrade', '_blank');
  };

  const handleVerifyPayment = () => {
    // Redirect to payment verification
    window.open('/verify-payment', '_blank');
  };

  const handleContactSupport = () => {
    // Open support chat or contact form
    window.open('mailto:support@lpds.com', '_blank');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!authorizationStatus) {
    return (
      <Alert severity="error">
        Unable to verify authorization status. Please contact support.
      </Alert>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Account Authorization
        </Typography>
        
        <Paper sx={{ p: 3, mt: 3 }}>
          {authorizationStatus.isAuthorized ? (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6">
                  ✅ Account Authorized
                </Typography>
                <Typography variant="body2">
                  {authorizationStatus.message}
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Subscription Details
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <Business />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Plan" 
                            secondary={authorizationStatus.subscriptionTier.toUpperCase()} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Status" 
                            secondary={authorizationStatus.subscriptionStatus} 
                          />
                        </ListItem>
                        {authorizationStatus.isTrialActive && (
                          <ListItem>
                            <ListItemIcon>
                              <AccessTime />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Trial Ends" 
                              secondary={new Date(authorizationStatus.trialEndsAt).toLocaleDateString()} 
                            />
                          </ListItem>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Available Features
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <Star />
                          </ListItemIcon>
                          <ListItemText primary="AI Content Generation" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Star />
                          </ListItemIcon>
                          <ListItemText primary="Multi-Platform Management" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Star />
                          </ListItemIcon>
                          <ListItemText primary="Analytics Dashboard" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Star />
                          </ListItemIcon>
                          <ListItemText primary="Lead Capture System" />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => onAuthorized(user, authorizationStatus)}
                >
                  Access Dashboard
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="h6">
                  ⚠️ Authorization Required
                </Typography>
                <Typography variant="body2">
                  {authorizationStatus.message}
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Payment sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Payment Verification Required
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        To access the full platform, please verify your payment method or upgrade your subscription.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleVerifyPayment}
                        sx={{ mb: 2 }}
                      >
                        Verify Payment
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={handleUpgrade}
                      >
                        Upgrade Plan
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Need Help?
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Contact our support team for assistance with account verification.
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleContactSupport}
                      >
                        Contact Support
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default UserAuthorization;
